import { Button, Stack, Text, useMantineTheme, SimpleGrid } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconCheck, IconRepeat, IconLockOpen, IconPlayerPause, IconUserPlus, IconX, IconArchive } from "@tabler/icons-react";
import { WorkOrderStatus } from "../../models/workorder.models";
import { useAccess } from "../../Auth/auth-functions";
import { AzureADRoles } from "../../Auth/auth-roles";

const statusOptions: { status: WorkOrderStatus; label: string; icon: React.ReactNode; color: string }[] = [
	{ status: WorkOrderStatus.Requested, label: "Requested", icon: <IconUserPlus size={16} />, color: "var(--mantine-color-primary-color)" },
	{ status: WorkOrderStatus.Open, label: "Open", icon: <IconLockOpen size={16} />, color: "var(--mantine-color-primary-color)" },
	{ status: WorkOrderStatus.OnHold, label: "On Hold", icon: <IconPlayerPause size={16} />, color: "var(--mantine-color-primary-color)" },
	{ status: WorkOrderStatus.InProgress, label: "In Progress", icon: <IconRepeat size={16} />, color: "var(--mantine-color-yellow)" },
	{ status: WorkOrderStatus.Completed, label: "Completed", icon: <IconCheck size={16} />, color: "var(--mantine-color-green)" },
	{ status: WorkOrderStatus.Archived, label: "Archived", icon: <IconArchive size={16} />, color: "var(--mantine-color-gray)" }
];

interface WorkOrderStatusProps {
	value: WorkOrderStatus;
	onChange: (status: WorkOrderStatus) => void;
	disabled?: boolean;
	onApprove?: () => void;
	isApproving?: boolean;
	onDecline?: () => void;
}

export const WorkOrderStatusPicker = ({ value, onChange, disabled, onApprove, isApproving, onDecline }: WorkOrderStatusProps) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);

	const isAdmin = useAccess(AzureADRoles.Admin);
	const canArchive = isAdmin && value === WorkOrderStatus.Completed;
	const showArchived = value === WorkOrderStatus.Archived || (value === WorkOrderStatus.Completed && canArchive);

	const filteredStatusOptions = statusOptions.filter(option => {
		if (value == WorkOrderStatus.Requested) {
			return option.status === WorkOrderStatus.Requested;
		} else {
			if (showArchived === false && option.status === WorkOrderStatus.Archived) {
				return false;
			} else {
				return option.status !== WorkOrderStatus.Requested;
			}
		}
	});

	return (
		<SimpleGrid cols={isMobile ? 2 : showArchived ? 5 : 4} spacing="xs" mb="md">
			{filteredStatusOptions.map((option, index) => (
				<Button key={index} w="100%" px={0} size="xl" radius="md" variant={value === option.status ? "filled" : "light"} onClick={() => onChange(option.status)} disabled={disabled} visibleFrom={isMobile && option.status === WorkOrderStatus.Requested ? "sm" : undefined}>
					<Stack align="center" justify="center" gap={0} style={{ width: '100%' }}>
						{option.icon}
						<Text fz="sm">{option.label}</Text>
					</Stack>
				</Button>
			))}

			{value == WorkOrderStatus.Requested && <>		
				{/* <Divider orientation="vertical" visibleFrom="xs" /> */}
				<Button w="100%" px={0} size="xl" radius="md" color="green" variant="filled" loading={isApproving} onClick={onApprove} disabled={disabled}>
					<Stack align="center" justify="center" gap={0} style={{ width: '100%' }}>
						<IconCheck size={16} />
						<Text fz="sm">Approve</Text>
					</Stack>
				</Button>
				<Button w="100%" px={0} size="xl" radius="md" color="red" variant="filled" onClick={onDecline} disabled={disabled}>
					<Stack align="center" justify="center" gap={0} style={{ width: '100%' }}>
						<IconX size={16} />
						<Text fz="sm">Decline</Text>
					</Stack>
				</Button>
			</>}
		</SimpleGrid>
	);
};
